import type { InjectionKey, Ref } from "vue";

import type { AccentTheme } from "@/pinia/theme";

// What the kit Dialog hands its panel: the element the caller wants focused on
// open. Headless UI took this on the dialog root; reka takes it on the content
// (openAutoFocus), so the root passes it down.
export interface KitDialogContext {
  initialFocus: Ref<unknown>;
  hasInitialFocus: Ref<boolean>;
  // role="alertdialog" from a destructive confirm, set on the root by callers.
  role: Ref<string | undefined>;
  // Whether a DialogDescription is mounted. Without one the panel drops
  // aria-describedby, which reka otherwise points at an id nothing carries.
  hasDescription: Ref<boolean>;
  // Only the dialog's own buttons close it (see Dialog.vue).
  required: Ref<boolean>;
  // Something was typed into the dialog since it opened, so an outside click,
  // easy to make by accident, would throw it away.
  dirty: Ref<boolean>;
  // What had focus when the dialog opened, to hand focus back to on close.
  opener: Ref<HTMLElement | null>;
  // Whether the caller placed a DialogOverlay. The panel draws it, in the same
  // portal and before itself (see DialogOverlay.vue).
  hasOverlay: Ref<boolean>;
}

export const KIT_DIALOG: InjectionKey<KitDialogContext> = Symbol("kit-dialog");

// The accent a panel takes when its caller names none. The user portal provides
// the admin's chosen colour (App.vue); the admin portal provides nothing, so its
// panels keep the brand rose.
export interface KitDialogTheme {
  readonly accent: AccentTheme;
  readonly highAccessibility: boolean;
}

export const KIT_DIALOG_THEME: InjectionKey<KitDialogTheme> = Symbol("kit-dialog-theme");
